import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import { useToast } from 'context/ToastContext';
import SolarIcon from 'components/icons/SolarIcon';

export interface ClientSelectedProps {
    value?: ClientType | null;
    onChange?: (client: ClientType | null) => void;
    onNewModeChange?: (isNewMode: boolean) => void;
    title?: string;
    rightElement?: ReactNode;
    maxHeight?: number;
    className?: string;
    disabled?: boolean;
    onPage?: number;
    clearable?: boolean;
    /** Дозволити режим «Новий клієнт» (value === null) */
    allowNew?: boolean;
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
    /** Фокус на полі пошуку після появи */
    autoFocus?: boolean;
}

const ClientNameItem = ({ client }: { client: ClientType }) => (
    <div className="min-w-0 overflow-hidden flex-grow-1">
        <h6 className="mb-1 text-truncate" title={client.clientName}>
            {client.clientName || '—'}
        </h6>
        <p className="text-muted f-12 mb-0 text-truncate" title={client.clientPhone || client.clientEmail || ''}>
            {client.clientPhone || client.clientEmail || ''}
        </p>
    </div>
);

const NewClientItem = () => {
    const { t } = useTranslation(['Clients']);
    return (
        <div className="min-w-0 overflow-hidden flex-grow-1">
            <h6 className="mb-1 text-truncate" title={t('newClient')}>
                {t('newClient')}
            </h6>
            <p className="text-muted f-12 mb-0 text-truncate">{t('newClientInfo')}</p>
        </div>
    );
};

const ClientSelected = ({
    value = null,
    onChange,
    onNewModeChange,
    title,
    rightElement = null,
    maxHeight = 220,
    className = '',
    disabled = false,
    onPage = 50,
    clearable = true,
    allowNew = false,
    preload = false,
    autoFocus = false,
}: ClientSelectedProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [clients, setClients] = useState<ClientType[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [picking, setPicking] = useState(false);
    const isInteractive = !disabled;
    const isNewMode = allowNew && !value && !picking;
    const isFixed = Boolean(value) || isNewMode;
    const canClear = clearable && isInteractive && Boolean(onChange);
    const showResults = !isFixed && (loading || searched);
    
    useEffect(() => {
        if (value) setPicking(false);
    }, [value]);


    useEffect(() => {
        onNewModeChange?.(isNewMode);
    }, [isNewMode, onNewModeChange]);

    if (preload) return null;




    const fetchClients = async () => {
        if (!isInteractive || loading || isFixed) return;

        setLoading(true);
        const res = await apiClient.getList({
            search: (search || '').trim(),
            page: 1,
            onPage,
            status: [0],
        });
        setLoading(false);
        setSearched(true);

        if (!res) {
            setClients([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setClients(res.clients || []);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchClients();
        }
    };

    const handleSelect = (client: ClientType) => {
        if (!isInteractive || !onChange) return;
        onChange(client);
        setSearch('');
        setClients([]);
        setSearched(false);
        setPicking(false);
    };

    const handleSelectNew = () => {
        if (!isInteractive || !onChange || !allowNew) return;
        onChange(null);
        setSearch('');
        setClients([]);
        setSearched(false);
        setPicking(false);
    };

    const handleClear = () => {
        if (!canClear || !onChange) return;

        if (value) {
            onChange(null);
            setSearch('');
            setClients([]);
            setSearched(false);
            setPicking(!allowNew);
            return;
        }

        if (isNewMode) {
            setPicking(true);
            setSearch('');
            setClients([]);
            setSearched(false);
        }
    };

    const labelText = title === undefined ? t('selectTitle') : title;

    return (
        <div className={`user-selected ${className || 'mb-3'}`.trim()}>
            {(labelText || rightElement) && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className={`form-label text-muted mb-1 ${labelText ? '' : 'd-none'}`}>
                        {labelText}
                    </label>
                    {rightElement}
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        {value ? <ClientNameItem client={value} /> : <NewClientItem />}
                    </div>
                    {canClear && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="user-selected-clear"
                            title={isNewMode ? t('selectSearchBtn') : t('common:buttons.clear')}
                            onClick={handleClear}
                        >
                            <SolarIcon name={isNewMode ? 'search' : 'x-lg'} />
                        </Button>
                    )}
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            autoFocus={autoFocus}
                            disabled={disabled || loading}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={fetchClients}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && clients.length === 0 && !allowNew && (
                                <div className="user-selected-empty">
                                    <SolarIcon name="person-x" className="fs-5 d-block mb-1 opacity-50" />
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && (allowNew || clients.length > 0) && (
                                <ul className="user-selected-list">
                                    {allowNew && (
                                        <li>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={handleSelectNew}
                                            >
                                                <NewClientItem />
                                                <SolarIcon name="plus-lg" className="user-selected-item-chevron opacity-75" />
                                            </button>
                                        </li>
                                    )}
                                    {clients.map((client) => (
                                        <li key={client.ID}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(client)}
                                            >
                                                <ClientNameItem client={client} />
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientSelected;
